import graphql from "babel-plugin-relay/macro";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { findLast } from "lodash";
import { useCallback } from "react";
import { useFragment } from "react-relay";
import { v4 as uuidv4 } from "uuid";

import { Box } from "@whoosh/design-system/src/components/primitive";

import { AddPatrons } from "../../../../../../features/AddPatrons";
import { AddPeopleSearch_Atom } from "../../../../../../features/AddPatrons/atoms/stores";
import { Patron } from "../../../../../../features/AddPatrons/types";
import { useFlags } from "../../../../../../utils/launchDarkly";
import { addPlayerAtom } from "../../../../atoms/actions/addPlayer/addPlayerAtom";
import { removePlayerAtom } from "../../../../atoms/actions/removePlayer/removePlayerAtom";
import { setNonPlayingHostOnRoundAtom } from "../../../../atoms/actions/setNonPlayingHostOnRoundAtom/setNonPlayingHostOnRoundAtom";
import { PlayersSelectorAtom } from "../../../../atoms/selectors/playersSelectorAtom/playersSelectorAtom";
import {
  BookingWizardDataAtom,
  CurrentRequestGroupIndexAtom,
  isAddingNewGuestAtom,
} from "../../../../atoms/stores";
import { BookingWizard } from "../../../../types";
import { AddPlayersHeader } from "./AddPlayersHeader";
import { GuestListHeader } from "./GuestListHeader";
import { MemberListHeader } from "./MemberListHeader";
import { AddPlayersPrivateStepClientProfile$key } from "./__generated__/AddPlayersPrivateStepClientProfile.graphql";
import { AddPlayersPrivateStepNode$key } from "./__generated__/AddPlayersPrivateStepNode.graphql";
import { useNewGuestSubmission } from "./hooks/useNewGuestSubmission";
import { getControlledCapacity as checkGetControlledCapacity } from "./utils/getControlledCapacity";
import { isBookingFull as checkIsBookingFull } from "./utils/isBookingFull";
import { isGroupFullOfGuests as checkIsGroupFullOfGuests } from "./utils/isGroupFullOfGuests";

type AddPlayersPrivateStepProps = {
  nodeRef: AddPlayersPrivateStepNode$key | null;
  clientProfileRef: AddPlayersPrivateStepClientProfile$key | null;
};

export const AddPlayersPrivateStep = ({
  nodeRef,
  clientProfileRef,
}: AddPlayersPrivateStepProps) => {
  const node = useFragment(AddPlayersPrivateStepNodeFragmentString, nodeRef);

  const bookingWizardData = useAtomValue(BookingWizardDataAtom);

  const isGolfCourse = node?.agenda?.facility?.type === "GOLF_COURSE";

  const addPlayerToBooking = useSetAtom(addPlayerAtom);
  const removePlayerFromBooking = useSetAtom(removePlayerAtom);
  const players = useAtomValue(PlayersSelectorAtom);

  const clientProfile = useFragment(
    AddPlayersPrivateStepFragmentString,
    clientProfileRef
  );

  const [search, setSearch] = useAtom(AddPeopleSearch_Atom);

  const setNonPlayingHostOnRound = useSetAtom(setNonPlayingHostOnRoundAtom);

  const [isAddingNewGuest, setIsAddingNewGuest] = useAtom(isAddingNewGuestAtom);

  const { membersCanAddUnknownGuests, memberNonPlayingHostToggle } = useFlags();

  const groupSize = bookingWizardData?.groupSize || 1;

  const allRounds = bookingWizardData?.rounds ?? [];
  const currentRequestGroupIndex = useAtomValue(CurrentRequestGroupIndexAtom);
  const rounds = allRounds.filter(
    (round) =>
      bookingWizardData?.type !== "RequestBooking" ||
      (round.bookingIndex ?? 0) === currentRequestGroupIndex
  );

  const memberHoldPlayers = rounds.filter(
    (round) => round.player.clientType === "memberHold"
  );

  const unknownGuestPlayers = rounds.filter(
    (round) => round.player.clientType === "unknownGuest"
  );

  const isBookingFull = checkIsBookingFull({
    nodeRequirePlayerCounts: node?.requirePlayerCounts,
    nodeStandardCapacity: node?.standardCapacity,
    nodeUsedCapacity: node?.usedCapacity,
    isGolfCourse,
    playersLength: players.length,
    groupSize,
    bookingType: bookingWizardData?.type,
    bookingWizardCapacity:
      bookingWizardData?.type === "RequestBooking"
        ? bookingWizardData?.capacity ?? 0
        : undefined,
  });

  const controlledCapacity = checkGetControlledCapacity({
    nodeRequirePlayerCounts: node?.requirePlayerCounts,
    nodeStandardCapacity: node?.standardCapacity,
    nodeUsedCapacity: node?.usedCapacity,
    isGolfCourse,
    bookingType: bookingWizardData?.type,
    groupSize,
    playersLength: players.length,
    bookingWizardCapacity:
      bookingWizardData?.type === "RequestBooking"
        ? bookingWizardData?.capacity ?? 0
        : undefined,
  });

  const isGroupFullOfGuests = checkIsGroupFullOfGuests({
    nodeRequirePlayerCounts: node?.requirePlayerCounts,
    nodeUsedCapacity: node?.usedCapacity,
    nodeStandardCapacity: memberNonPlayingHostToggle
      ? (node?.standardCapacity ?? 0) + 1
      : node?.standardCapacity ?? 0,
    isGolfCourse,
    playersLength: players.length,
    groupSize: memberNonPlayingHostToggle ? groupSize + 1 : groupSize,
    bookingType: bookingWizardData?.type,
    bookingWizardCapacity:
      bookingWizardData?.type === "RequestBooking"
        ? bookingWizardData?.capacity ?? 0
        : undefined,
  });

  const onNewGuestSubmission = useNewGuestSubmission(isBookingFull);

  const capacityOnBooking = controlledCapacity !== 0 ? controlledCapacity : 4;

  const allPlayerIds = allRounds.flatMap((round) =>
    round.player.id ? [round.player.id] : []
  );
  const currentPlayers = rounds.flatMap((round) =>
    round.player.id ? [round.player.id] : []
  );
  const disabledPlayerIds = allPlayerIds.filter(
    (playerId) => !currentPlayers.includes(playerId)
  );

  const currentPatron: Patron = {
    playerId: clientProfile?.member?.id || "",
    firstName: clientProfile?.member?.firstName || "",
    lastName: clientProfile?.member?.lastName || "",
    profileImageId: clientProfile?.member?.profileImage?.publicId || "",
    type: "Member",
    memberGroups:
      clientProfile?.member?.memberGroups?.flatMap((g) =>
        g?.id ? [g.id] : []
      ) ?? [],
  };

  const memberRounds = rounds.filter((round) =>
    ["member", "memberHold"].includes(round.player.clientType)
  );

  const showGuestListOverlay = memberRounds.length === 0;

  const onCheckPlayer = useCallback(
    (player: BookingWizard.BookingPlayer, checked: boolean) => {
      if (checked) {
        // Map the BookingPlayer to the appropriate RoundPlayer type based on player type
        let roundPlayer: BookingWizard.RoundPlayer;

        if (player.type === "Member") {
          roundPlayer = {
            id: player.playerId || player.internalIdentifier || uuidv4(),
            clientType: "member" as const,
            firstName: player.firstName || null,
            lastName: player.lastName || null,
            profileImagePublicId: player.profileImageId || null,
          };
        } else if (player.type === "Guest") {
          roundPlayer = {
            id: player.playerId || player.internalIdentifier || uuidv4(),
            clientType: "guest" as const,
            firstName: player.firstName || null,
            lastName: player.lastName || null,
            profileImagePublicId: player.profileImageId || null,
          };
        } else if (player.type === "Unknown Guest") {
          roundPlayer = {
            id: null,
            clientType: "unknownGuest" as const,
            firstName: null,
            lastName: null,
          };
        } else if (player.type === "Member Hold") {
          roundPlayer = {
            id: player.playerId || player.internalIdentifier || uuidv4(),
            clientType: "memberHold" as const,
            firstName: player.firstName || null,
            lastName: player.lastName || null,
            profileImagePublicId: player.profileImageId || null,
          };
        } else {
          // Default to guest if type is not recognized
          roundPlayer = {
            id: player.playerId || player.internalIdentifier || uuidv4(),
            clientType: "guest" as const,
            firstName: player.firstName || null,
            lastName: player.lastName || null,
            profileImagePublicId: player.profileImageId || null,
          };
        }
        addPlayerToBooking(roundPlayer);
        // If over capacity and memberNonPlayingHost is true, set the first member round (owner) to non-playing host
        if (
          isBookingFull &&
          (roundPlayer.clientType === "guest" ||
            roundPlayer.clientType === "unknownGuest") &&
          memberNonPlayingHostToggle
        ) {
          const ownerMemberRound = memberRounds[0];
          setNonPlayingHostOnRound(
            { playerId: ownerMemberRound?.player?.id ?? "" },
            true
          );
        }
      } else {
        removePlayerFromBooking(
          player.playerId || player.internalIdentifier || ""
        );
      }
    },
    [
      addPlayerToBooking,
      removePlayerFromBooking,
      isBookingFull,
      memberRounds,
      setNonPlayingHostOnRound,
      memberNonPlayingHostToggle,
    ]
  );

  const onAddUnknownGuest = useCallback(() => {
    const unknownGuestPlayer: BookingWizard.RoundPlayer = {
      id: uuidv4(),
      clientType: "unknownGuest" as const,
      firstName: null,
      lastName: null,
    };

    addPlayerToBooking(unknownGuestPlayer);
    if (memberNonPlayingHostToggle && isBookingFull) {
      const ownerMemberRound = memberRounds[0];
      setNonPlayingHostOnRound(
        { playerId: ownerMemberRound?.player?.id ?? "" },
        true
      );
    }
  }, [
    addPlayerToBooking,
    memberNonPlayingHostToggle,
    isBookingFull,
    memberRounds,
    setNonPlayingHostOnRound,
  ]);

  const onRemoveUnknownGuest = useCallback(() => {
    const guestToRemove = findLast(
      players,
      (p) => p.clientType === "unknownGuest"
    );
    if (guestToRemove) {
      removePlayerFromBooking(guestToRemove.id ?? "");
    }
  }, [players, removePlayerFromBooking]);

  const allowUnknownGuest = membersCanAddUnknownGuests;

  return (
    <>
      <Box
        flex={1}
        safeAreaTop
        backgroundColor="surface-primary"
        testID="add-players-private-step-container"
      >
        <AddPlayersHeader nodeRef={node ?? null} isGolfCourse={isGolfCourse} />
        <AddPatrons
          keepItemInListAfterCheck={true}
          includeTransportationType={isGolfCourse}
          currentPatron={currentPatron}
          search={search}
          setSearch={setSearch}
          handleAddMemberClick={onCheckPlayer}
          handleAddGuestClick={onCheckPlayer}
          showGuestListOverlay={showGuestListOverlay}
          isGroupFullOfAttendees={false}
          isGroupFullOfGuests={
            memberNonPlayingHostToggle ? isGroupFullOfGuests : isBookingFull
          }
          isGroupFullOfMembers={isBookingFull}
          checkNewDisabled={
            memberNonPlayingHostToggle ? isGroupFullOfGuests : isBookingFull
          }
          areGuestsAllowed={true}
          unknownGuestsEnabled={membersCanAddUnknownGuests}
          canAddNewGuest={true}
          isAddingNewGuest={isAddingNewGuest}
          setIsAddingNewGuest={setIsAddingNewGuest}
          onNewGuestSubmission={onNewGuestSubmission}
          onAddUnknownGuest={onAddUnknownGuest}
          onRemoveUnknownGuest={onRemoveUnknownGuest}
          bg="surface-primary"
          players={currentPlayers}
          allPlayers={allPlayerIds}
          disabledPlayerIds={disabledPlayerIds}
          unknownGuestCount={unknownGuestPlayers?.length ?? 0}
          memberHoldCount={memberHoldPlayers?.length ?? 0}
          memberListHeader={
            <MemberListHeader
              capacityOnBooking={capacityOnBooking}
              rounds={rounds}
            />
          }
          guestListHeader={
            <GuestListHeader
              onAddUnknownGuest={onAddUnknownGuest}
              onRemoveUnknownGuest={onRemoveUnknownGuest}
              allowUnknownGuest={allowUnknownGuest}
              capacityOnBooking={capacityOnBooking}
              rounds={rounds}
            />
          }
        />
      </Box>
    </>
  );
};

export const AddPlayersPrivateStepNodeFragmentString = graphql`
  fragment AddPlayersPrivateStepNode on Node {
    ...AddPlayersHeaderNode
    ... on TimeSlot {
      requirePlayerCounts
      standardCapacity: capacity(bookingType: STANDARD)
      usedCapacity
      agenda {
        facility {
          type
        }
      }
    }
  }
`;

export const AddPlayersPrivateStepFragmentString = graphql`
  fragment AddPlayersPrivateStepClientProfile on ClientProfile {
    member {
      clientClass
      ... on Member {
        id
        firstName
        lastName
        profileImage {
          # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
          publicId: renderingKey
        }
        memberGroups {
          id
        }
      }
    }
  }
`;
